import React, { useCallback, useEffect } from "react";
import ThreeMeshUI from "three-mesh-ui";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Hotspot, Popup } from "../../../../../Types";
import { UseDownloadAsset } from "../../../../../api/calls/Queries";
import { useController } from "@react-three/xr";
import { Box, Flex, useReflow } from "@react-three/flex";
import { Text, useTexture } from "@react-three/drei";
import Spinner from "../../FixedComponents/Spinner";
import CloseBtn from "../../FixedComponents/CloseBtn";

const XRImagePop = (props: { currentPop: Popup; hs: Hotspot }) => {
  useFrame(() => {
    ThreeMeshUI.update();
  });

  const { data } = UseDownloadAsset(props.currentPop.image!);

  const leftController = useController("left");
  const rightController = useController("right");
  const gazeController = useController("none");

  return (
    <Flex
      align={"center"}
      onUpdate={(self) => {
        self.lookAt(0, 0, 0);
      }}
      alignItems={"flex-start"}
      justifyContent={"center"}
      scale={gazeController ? 7 : 3.5}
      position={[props.hs.x, props.hs.y, props.hs.z]}
    >
      <WholeWrapper currentPop={props.currentPop} imgBlob={data} />
    </Flex>
  );
};

export default XRImagePop;

const img = new Image();
const WholeWrapper = (props: {
  currentPop: Popup;
  imgBlob: string | undefined;
}) => {
  const { currentPop, imgBlob } = props;

  const reflow = useReflow();
  const [ratio, setRatio] = React.useState<number | undefined>();

  useEffect(() => {
    //@ts-ignore
    img.src = null;
    setRatio(undefined);

    if (imgBlob) {
      img.src = imgBlob;
      img.onload = () => {
        setRatio(img.naturalWidth / img.naturalHeight);
      };
    }
  }, [setRatio, imgBlob]);

  return (
    <>
      {imgBlob && ratio ? (
        <>
          <Box marginBottom={1} dir={"row"}>
            <Box width={30} centerAnchor>
              <Text
                material={textMeshMaterial}
                renderOrder={3}
                fontSize={2}
                fontWeight={800}
                textAlign="center"
                maxWidth={30}
                overflowWrap="break-word"
              >
                {currentPop.title}
              </Text>
            </Box>

            <Box centerAnchor>
              <CloseBtn scale={1} />
            </Box>
          </Box>
          <Box marginTop={1}>
            <Box
              flexDir={"column"}
              alignItems={"center"}
              // justifyContent={"center"}
              // dir={ratio > 1 ? "column" : "row"}
              // align={"center"}
              // justify={"center"}
            >
              <ImageAndText
                ratio={ratio}
                imgBlob={imgBlob}
                text={currentPop.text}
              />
            </Box>
          </Box>
        </>
      ) : (
        <Spinner scale={1} />
      )}
    </>
  );
};

const ImageAndText = (props: {
  ratio: number;
  imgBlob: string;
  text: string | undefined;
}) => {
  const { ratio, imgBlob, text } = props;

  const map = useTexture(imgBlob);
  const reflow = useReflow();
  useFrame(() => {
    reflow();
  });

  const width: (ratio: number) => number = useCallback((ratio: number) => {
    if (ratio > 1) return 30;
    else return ratio * 30;
  }, []);

  const height: (ratio: number) => number = useCallback((ratio: number) => {
    if (ratio < 1) return 30;
    else return 30 / ratio;
  }, []);

  return (
    <>
      <Box centerAnchor>
        <mesh
          renderOrder={3}
          geometry={new THREE.PlaneGeometry(width(ratio), height(ratio))}
          material={
            new THREE.MeshBasicMaterial({
              map: map,
              transparent: true,
              depthTest: false,
            })
          }
        ></mesh>
      </Box>

      <Box margin={1} centerAnchor>
        <Text
          renderOrder={3}
          maxWidth={ratio > 1 ? 30 : 10}
          fontSize={2}
          material={textMeshMaterial}
        >
          {text}
        </Text>
      </Box>
    </>
  );
};

export const textMeshMaterial = new THREE.MeshBasicMaterial({
  color: "white",
  transparent: true,
  depthTest: false,
});
